import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  BAND_LEVELS,
  type BandPart,
  VERDICT_TONE,
  bandNamesParts,
  bandNothingWords,
  bandSourceWords,
  levelLetter,
  levelSignWords,
  placementParts,
  principalSentence,
} from '@/lib/expected-noyau-band';
import { isExpectedTriplet, verdictWords } from '@/lib/expected-noyau-words';
import { noyauPillInkClass } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import {
  type ExpectedNoyauReading,
  type NoyauCode,
  type NoyauScore,
  type ReplayMarkNpc,
  noyauOf,
} from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * The band « Face à l'attendu » of the block « Détermination du noyau » (npc.md §26.3, issue
 * #216, lot K; maquettes `Main.dc.html` and `Cas.dc.html` of `docs/specs/maquettes/npc/attendu/`):
 * the noyau the NPC was EXPECTED to give, against the one the session FOUND, between the facts of
 * the block and its six notes.
 *
 * From left to right: its title and where the expected noyau came from (a link to THAT version of
 * the NPC's sheet); a small table of the three levels - the letter expected, the sign and the word
 * (« = tient », « ≠ change »), the letter found - in tiles of 28 px at the colour of their noyau;
 * the two noyaux in full words, the verdict in words, where the expected noyau falls (its rank, its
 * gap, whether it was one of the two cards); and, when the principal of the analyst is not the
 * retained noyau, the line that says why.
 *
 * ## Nothing is derived here
 *
 * The server computes the verdict, the rank, the gap and the cards (`compareNoyaux`,
 * `placeExpectedNoyau` of `shared`, served as `replay.expected`); this component draws them with
 * the words of `lib/expected-noyau-band.ts`. The verdict is said in WORDS, with a sign per level -
 * never by a colour alone.
 *
 * ## The states
 *
 * A verdict (the table and the sentences); the same under « attendu actuel » when the replay played
 * no version; no found noyau (the expected letters face an empty tile « ? », and the pill says
 * « sans extrait », « en attente de la détermination » or « en échec »); an expected noyau « hors
 * base » (nothing to compare, no table). The block draws NO band at all for a trial of the analyst,
 * a real session, a replay with no NPC or an NPC with no expected noyau: it never mounts this
 * component then.
 */

export interface WalkNoyauExpectedBandProps {
  readonly expected: ExpectedNoyauReading;
  /** The NPC the replay played, for its name and the link to the version; null once it is gone. */
  readonly npc: Pick<ReplayMarkNpc, 'id' | 'name' | 'versionId'> | null;
  /** The six notes of the determination, for the line of the principal. */
  readonly scores: readonly Pick<NoyauScore, 'noyau' | 'score'>[];
  /** The threshold frozen at the deal of the first choice, null before there was one or without a first choice. */
  readonly threshold: number | null;
}

/** A tile of 28 px: the letter of a noyau at a level, in the colour of that noyau. */
function Tile({
  code,
  letter,
  testId,
}: {
  code: NoyauCode | null;
  letter: string;
  testId?: string;
}) {
  if (code === null) {
    return (
      <span
        data-testid={testId}
        className="inline-flex size-7 items-center justify-center rounded-sm border border-gray-400 border-dashed bg-white font-bold text-gray-600"
      >
        <span aria-hidden="true">?</span>
        <span className="sr-only">aucun noyau trouvé</span>
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-sm font-bold',
        noyauPillInkClass(code),
      )}
      style={{ backgroundColor: noyauOf(code).color }}
    >
      {letter}
    </span>
  );
}

function Parts({ parts }: { parts: readonly BandPart[] }): ReactNode {
  return parts.map((part, index) =>
    part.strong ? (
      // The parts of a sentence are fixed and never reordered: their rank is their key.
      // biome-ignore lint/suspicious/noArrayIndexKey: a sentence is a fixed run of parts
      <b key={index} className="font-semibold text-gray-900">
        {part.text}
      </b>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: a sentence is a fixed run of parts
      <span key={index}>{part.text}</span>
    ),
  );
}

export function WalkNoyauExpectedBand({
  expected: reading,
  npc,
  scores,
  threshold,
}: WalkNoyauExpectedBandProps) {
  const { expected, provenance, found, comparison, placement, principal } = reading;
  const triplet = isExpectedTriplet(expected) ? expected : null;
  const state = triplet === null ? 'out-of-base' : found === null ? 'no-found' : 'compared';
  const source = bandSourceWords(provenance, npc?.name);
  const sourceClass =
    'font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit';

  return (
    <section
      aria-labelledby="walk-noyau-expected-title"
      data-testid={SESSIONS_TEST_IDS.walkNoyauExpected}
      data-state={state}
      data-verdict={comparison?.verdict}
      data-provenance={provenance.kind}
      data-determination={reading.determinationState ?? undefined}
      data-expected={expected}
      data-found={found ?? undefined}
      className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-md bg-gray-50 px-4 py-3"
    >
      <div className="flex w-full flex-col gap-1 sm:w-40">
        <h4 id="walk-noyau-expected-title" className="font-semibold text-body text-gray-900">
          Face à l’attendu
        </h4>
        <span
          data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedSource}
          data-provenance={provenance.kind}
          className="text-caption text-gray-600"
        >
          {source.lead}
          {npc === null ? (
            source.owner
          ) : (
            <Link
              to="/admin/npc/$npcId"
              params={{ npcId: npc.id }}
              // The version the replay PLAYED; the current one has no version to name.
              search={provenance.kind === 'played' ? { version: npc.versionId } : {}}
              className={sourceClass}
            >
              {source.owner}
            </Link>
          )}
          {source.tail}
        </span>
      </div>

      {triplet === null ? null : (
        <table className="border-collapse text-small">
          <thead>
            <tr>
              <th scope="col" className="pr-3 pb-1 text-left font-normal">
                <span className="sr-only">Niveau</span>
              </th>
              <th scope="col" className="pr-3 pb-1 text-center font-normal text-gray-600">
                attendu
              </th>
              <th scope="col" className="pr-3 pb-1 text-center font-normal">
                <span className="sr-only">Même énergie</span>
              </th>
              <th scope="col" className="pb-1 text-center font-normal text-gray-600">
                trouvé
              </th>
            </tr>
          </thead>
          <tbody>
            {BAND_LEVELS.map(({ level, label }) => {
              const holds = comparison?.levels[level] ?? null;
              const sign = holds === null ? null : levelSignWords(holds);
              return (
                <tr
                  key={level}
                  data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedLevel}
                  data-level={level}
                  data-holds={holds === null ? undefined : String(holds)}
                >
                  <th scope="row" className="py-0.5 pr-3 text-left font-semibold text-gray-600">
                    {label}
                  </th>
                  <td className="py-0.5 pr-3 text-center">
                    <Tile code={triplet} letter={levelLetter(triplet, level)} />
                  </td>
                  <td
                    className={cn(
                      'whitespace-nowrap py-0.5 pr-3 text-center font-bold',
                      holds === null ? null : holds ? 'text-vert-deep' : 'text-rouge-deep',
                    )}
                  >
                    {sign === null ? null : (
                      <>
                        {sign.sign} <span className="font-semibold text-caption">{sign.word}</span>
                      </>
                    )}
                  </td>
                  <td className="py-0.5 text-center">
                    <Tile code={found} letter={levelLetter(found, level)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="flex min-w-0 flex-1 basis-72 flex-col gap-2">
        <p
          data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedNames}
          className="text-gray-900 text-small leading-normal"
        >
          <Parts parts={bandNamesParts(triplet, found)} />
        </p>
        {comparison === null ? (
          <span
            data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedVerdict}
            className="inline-flex self-start rounded-full border border-gray-400 bg-white px-2.5 py-0.5 font-bold text-gray-700 text-small"
          >
            {bandNothingWords(triplet === null, reading.determinationState)}
          </span>
        ) : (
          // StatusBadge takes no className: the wrapper places it, the pill keeps its own weight.
          <span className="self-start">
            <StatusBadge
              tone={VERDICT_TONE[comparison.verdict]}
              size="sm"
              data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedVerdict}
            >
              {verdictWords(comparison)}
            </StatusBadge>
          </span>
        )}
        {placement === null ? null : (
          <p
            data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedPlace}
            className="text-gray-600 text-small leading-normal"
          >
            <Parts parts={placementParts(placement)} />
          </p>
        )}
        {principal === null || found === null ? null : (
          <p
            data-testid={SESSIONS_TEST_IDS.walkNoyauExpectedPrincipal}
            className="text-bleu-nuit-deep text-small leading-normal"
          >
            {principalSentence({ principal, retained: found, scores, threshold })}
          </p>
        )}
      </div>
    </section>
  );
}
